// @ts-check

import React, { createContext, useContext, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { keepPreviousData } from "@tanstack/react-query";

import { useGlobalContext } from "context/GlobalContext";

import { useAppQuery } from "hooks/use-app-query";

import { ORDERS_PAGE_SIZE } from "utils/orders";

const UtmSetContext = createContext({});

export const UtmSetProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const { dates } = useGlobalContext();

  const [utmSetOrdersPage, setUtmSetOrdersPage] = useState(1);

  const utmSetOrdersUrl = useMemo(() => {
    const params = {
      page: utmSetOrdersPage,
      page_size: ORDERS_PAGE_SIZE,
      browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (dates) {
      params.start_date = dates.start.toISOString();
      params.end_date = dates.end.toISOString();
    }

    if (searchParams.get("utm_source")) {
      params.utm_source = searchParams.get("utm_source");
    }

    if (searchParams.get("utm_medium")) {
      params.utm_medium = searchParams.get("utm_medium");
    }

    if (searchParams.get("utm_campaign")) {
      params.utm_campaign = searchParams.get("utm_campaign");
    }

    if (searchParams.get("utm_content")) {
      params.utm_content = searchParams.get("utm_content");
    }

    if (searchParams.get("utm_term")) {
      params.utm_term = searchParams.get("utm_term");
    }

    return `/api/utm-sets/orders?${new URLSearchParams(params).toString()}`;
  }, [dates, searchParams, utmSetOrdersPage]);

  const {
    data: utmSetOrdersResponse,
    refetch: refetchUtmSetOrders,
    isFetching: isFetchingUtmSetOrders,
  } = useAppQuery({
    url: utmSetOrdersUrl,
    extraQueryKeys: ["utmSetOrders"],
    reactQueryOptions: {
      enabled: dates !== null,
      staleTime: 10 * 60 * 1000,
      placeholderData: keepPreviousData,
    },
  });

  const utmSetOrders = utmSetOrdersResponse?.utmSetOrders || [];
  const utmSetError = utmSetOrdersResponse?.error || null;
  const utmSetOrdersTotalCount = utmSetOrdersResponse?.totalCount || 0;

  return (
    <UtmSetContext.Provider
      value={{
        utmSetOrders,
        refetchUtmSetOrders,
        isFetchingUtmSetOrders,
        utmSetError,
        utmSetOrdersTotalCount,

        utmSetOrdersPage,
        setUtmSetOrdersPage,
      }}
    >
      {children}
    </UtmSetContext.Provider>
  );
};

export const useUtmSetContext = () => {
  const context = useContext(UtmSetContext);

  return context;
};
